import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router'
import { api } from '../utils/api'
import { formatDate, formatTime, getMeetupStatus, isMeetupUpcoming } from '../utils/helpers'
import {
  CalendarIcon,
  MapPinIcon,
  UserGroupIcon,
  ClockIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import AuthFlow from '../components/AuthFlow'
import { useAuth } from '../hooks/useAuth'
import ReactMarkdown from 'react-markdown'
import { withLineBreaks } from '../utils/markdown'

const MeetupDetail = () => {
  const { id } = useParams()
  const { user } = useAuth()
  const [meetup, setMeetup] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAuthFlow, setShowAuthFlow] = useState(false)

  useEffect(() => {
    window.scrollTo(0, 0)

    const fetchMeetup = async () => {
      try {
        const response = await api.get(`/meetups/${id}`)
        setMeetup(response.data.meetup)
      } catch (error) {
        console.error('Meetup ophalen fout:', error)
        if (error.response?.status === 404) {
          setError('Meetup niet gevonden')
        } else {
          setError('Kon meetup niet laden')
        }
        toast.error('Kon meetup details niet laden')
      } finally {
        setLoading(false)
      }
    }

    fetchMeetup()
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Meetup laden...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {error}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              De meetup die je zoekt bestaat niet of is niet meer beschikbaar.
            </p>
            <Link to="/" className="btn btn-primary">
              Terug naar home
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const status = getMeetupStatus(meetup.date, meetup.startTime)
  const spotsLeft = meetup.registrationCounts.available
  const isUpcoming = isMeetupUpcoming(meetup.date, meetup.startTime)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-500 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Terug naar overzicht
          </Link>
        </div>

        {/* Main content */}
        <div className="card">
          {/* Header */}
          <div className="card-header">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <span className={`inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium ${
                    status.color === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                    status.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' :
                    'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                  }`}>
                    {status.label}
                  </span>
                  {isUpcoming && spotsLeft > 0 && spotsLeft <= 5 && (
                    <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                      Nog {spotsLeft} plekken
                    </span>
                  )}
                  {isUpcoming && spotsLeft === 0 && (
                    <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                      Vol
                    </span>
                  )}
                </div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {meetup.title}
                </h1>
              </div>
            </div>
          </div>

          <div className="card-body">
            {/* Info grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CalendarIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Datum</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatDate(meetup.date)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <ClockIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Tijd</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatTime(meetup.startTime)} - {formatTime(meetup.endTime)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <MapPinIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Locatie</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {meetup.location}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <UserGroupIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Deelnemers</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {meetup.maxParticipants != null
                        ? `${meetup.registrationCounts.confirmed} van ${meetup.maxParticipants}`
                        : `${meetup.registrationCounts.confirmed} aangemeld`}
                      {meetup.registrationCounts.waitlist > 0 && (
                        <span className="text-gray-500 dark:text-gray-400 ml-1">
                          (+{meetup.registrationCounts.waitlist} wachtlijst)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Over deze meetup
              </h2>
              <div className="markdown-body bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-300 dark:border-gray-400 dark:**:text-white! dark:[&_a]:text-primary-400! dark:[&_code]:bg-gray-700! dark:[&_pre]:bg-gray-700!">
                <ReactMarkdown
                  components={{
                    a: ({ node, children, href, ...props }) => (
                      <a href={href} target="_blank" rel="noopener noreferrer" {...props} className="inline-flex items-center gap-1">
                        {children}
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 inline-block" />
                      </a>
                    )
                  }}
                >
                  {withLineBreaks(meetup.description)}
                </ReactMarkdown>
              </div>
            </div>

            {/* Attendees */}
            {meetup.attendees && (meetup.attendees.confirmed.length > 0 || meetup.attendees.waitlist.length > 0) && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Aanwezigen
                </h2>

                {meetup.attendees.confirmed.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Bevestigde deelnemers ({meetup.attendees.confirmed.length})
                    </h3>
                    {user ? (
                      <div className="flex flex-wrap gap-2">
                        {meetup.attendees.confirmed.map((attendee, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                          >
                            {attendee.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-600 dark:text-gray-400">
                        Log in om de namen van deelnemers te zien
                      </p>
                    )}
                  </div>
                )}

                {meetup.attendees.waitlist.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Wachtlijst ({meetup.attendees.waitlist.length})
                    </h3>
                    {user ? (
                      <div className="flex flex-wrap gap-2">
                        {meetup.attendees.waitlist.map((attendee, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300"
                          >
                            {attendee.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-600 dark:text-gray-400">
                        Log in om de namen van deelnemers te zien
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Registration info */}
            {isUpcoming && !user && (
              <>
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
                    Hoe deel te nemen?
                  </h3>
                  <div className="space-y-2 text-blue-800 dark:text-blue-300">
                    <p>1. Schrijf je in voor onze nieuwsbrief via de knop hieronder</p>
                    <p>2. Je ontvangt automatisch een email met meetup details</p>
                    <p>3. Reageer in de email met &ldquo;Ik kom&rdquo; of &ldquo;Ik kom niet&rdquo;</p>
                    {spotsLeft === 0 && (
                      <p className="font-medium">
                        ⚠️ Deze meetup is vol, maar je kunt je nog steeds aanmelden voor de wachtlijst!
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-center flex justify-center items-center gap-4">
                  <button
                    onClick={() => setShowAuthFlow(true)}
                    className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors w-40"
                  >
                    Schrijf je in!
                  </button>
                  <span className="text-gray-600 dark:text-gray-400 font-medium">of</span>
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-gray-900 bg-primary-400 hover:bg-primary-500 dark:bg-primary-400 dark:hover:bg-primary-500 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors w-40"
                  >
                    Login
                  </Link>
                </div>
              </>
            )}

            {!isUpcoming && !user && (
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Deze meetup is afgelopen
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Schrijf je in voor onze nieuwsbrief om op de hoogte te blijven van nieuwe meetups!
                </p>
                <button
                  onClick={() => setShowAuthFlow(true)}
                  className="btn btn-outline mt-4"
                >
                  Inschrijven voor updates
                </button>
              </div>
            )}
          </div>
        </div>

        {/* More meetups */}
        <div className="mt-8 text-center">
          <Link
            to="/#meetups"
            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-500 font-medium transition-colors"
          >
            Bekijk alle meetups →
          </Link>
        </div>
      </div>

      {/* AuthFlow modal */}
      {showAuthFlow && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"
              onClick={() => setShowAuthFlow(false)}
            />
            <div className="relative inline-block bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl sm:my-8 sm:max-w-lg sm:w-full sm:p-6">
              <button
                onClick={() => setShowAuthFlow(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
              <AuthFlow onSuccess={() => setShowAuthFlow(false)} onClose={() => setShowAuthFlow(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MeetupDetail